import React from 'react';
import { Spinner } from 'react-bootstrap';
import { FaIdCard, FaUniversity, FaGraduationCap, FaBook, FaCalendar, FaKeyboard, FaHashtag } from 'react-icons/fa';
import GenericCrudPage from '../components/GenericCrudPage';
import {
  useColleges,
  useDegrees,
  useProgrammes,
  useAcademicYears,
} from '../hooks/useDropdownData';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const StudentIdFormat = () => {
  const { colleges, loading: collegesLoading } = useColleges();
  const { degrees, loading: degreesLoading } = useDegrees();
  const { programmes, loading: programmesLoading } = useProgrammes();
  const { academicYears, loading: academicYearsLoading } = useAcademicYears();

  const dropdownsLoading = collegesLoading || degreesLoading || programmesLoading || academicYearsLoading;

  const config = {
    baseUrl: BASE_URL,
    endpoints: {
      fetch: '/academic/getStudentIdFormat',
      save: '/academic/createStudentIdFormat',
      update: '/academic/updateStudentIdFormat',
      delete: '/academic/deleteStudentIdFormat',
    },
    entityName: 'Student ID Format',
    deleteMethod: 'GET',
    transformResponse: (response) => response.data?.studentidformat || [],

    validateForm: (form) => {
      const errors = {};
      if (!form.colleges) errors.colleges = 'College is required';
      if (!form.degree) errors.degree = 'Degree is required';
      if (!form.programmes) errors.programmes = 'Program is required';
      if (!form.academicYear) errors.academicYear = 'Academic Year is required';
      if (!form.exStartWith?.trim()) errors.exStartWith = 'Prefix is required';
      if (!form.exIdValue?.trim()) errors.exIdValue = 'Next ID Value is required';
      return errors;
    },

    initialFormState: {
      colleges: '',
      programmes: '',
      academicYear: '',
      degree: '',
      exIdValue: '',
      exStartWith: '',
      exIsActive: 1,
      user: 1,
    },
  };

  const transformPayload = (form, editId, userId) => {
    const payload = {
      colleges: Number(form.colleges),
      programmes: Number(form.programmes),
      academicYear: Number(form.academicYear),
      degree: Number(form.degree),
      exIdValue: form.exIdValue.trim(),
      exStartWith: form.exStartWith.trim(),
      exIsActive: form.exIsActive ? 1 : 0,
      user: userId,
      exLocalLangName: " ",
    };
    if (editId) {
      payload.id = editId;
    }
    return payload;
  };

  const transformToForm = (item) => ({
    colleges: item.collegesId?.toString() || '',
    programmes: item.programmesId?.toString() || '',
    academicYear: item.academicyearId?.toString() || '',
    degree: item.DegreeId?.toString() || '',
    exIdValue: item.exIdValue || '',
    exStartWith: item.exStartWith || '',
    exIsActive: item.exIsActive === true || item.exIsActive === 1,
    user: 1,
  });

  const formFields = [
    {
      title: 'Format Configuration',
      fields: [
        { name: 'degree', label: 'Degree', icon: FaGraduationCap, required: true, as: 'select', options: (degrees || []).map(d => ({ value: d.id, label: d.exName })) },
        { name: 'colleges', label: 'College', icon: FaUniversity, required: true, as: 'select', options: (colleges || []).map(c => ({ value: c.id, label: c.exName })) },
        { name: 'programmes', label: 'Program', icon: FaBook, required: true, as: 'select', options: (programmes || []).map(p => ({ value: p.id, label: p.exName })) },
        { name: 'academicYear', label: 'Academic Year', icon: FaCalendar, required: true, as: 'select', options: (academicYears || []).map(a => ({ value: a.id, label: a.exYear })) },
        { name: 'exStartWith', label: 'ID Prefix', icon: FaKeyboard, required: true, placeholder: 'e.g., PALB' },
        { name: 'exIdValue', label: 'Next ID Value', icon: FaHashtag, required: true, placeholder: 'e.g., 9125' },
        { name: 'exIsActive', label: 'Active Status', icon: FaIdCard, as: 'switch' },
      ],
    },
  ];

  const tableColumns = [
    { key: 'colleges', label: 'College', sortable: true },
    { key: 'Degree', label: 'Degree', sortable: true },
    { key: 'programmes', label: 'Program', sortable: true },
    { key: 'academicyear', label: 'Academic Year', sortable: true },
    { key: 'exStartWith', label: 'Prefix', sortable: true },
    { key: 'exIdValue', label: 'Next Value', sortable: true },
  ];

  const getViewFields = (item) => {
    if (!item) return [];
    return [
      { label: 'College', value: item.colleges, icon: FaUniversity },
      { label: 'Degree', value: item.Degree, icon: FaGraduationCap },
      { label: 'Program', value: item.programmes, icon: FaBook },
      { label: 'Academic Year', value: item.academicyear, icon: FaCalendar },
      { label: 'ID Prefix', value: item.exStartWith, icon: FaKeyboard },
      { label: 'Next ID Value', value: item.exIdValue, icon: FaHashtag },
      { label: 'Status', value: item.exIsActive ? 'Active' : 'Inactive', icon: FaIdCard },
    ];
  };

  console.log('Programmes data:', programmes);
  console.log('Programmes loading:', programmesLoading);

  if (dropdownsLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading configuration...</p>
      </div>
    );
  }

  return (
    <GenericCrudPage
      title="Student ID Format"
      config={config}
      formFields={formFields}
      tableColumns={tableColumns}
      getViewFields={getViewFields}
      transformPayload={transformPayload}
      transformToForm={transformToForm}
      emptyIcon={FaIdCard}
      searchPlaceholder="Search by prefix, college, degree..."
      csvHeaders={[
        { key: 'colleges', label: 'College' },
        { key: 'Degree', label: 'Degree' },
        { key: 'programmes', label: 'Program' },
        { key: 'academicyear', label: 'Academic Year' },
        { key: 'exStartWith', label: 'Prefix' },
        { key: 'exIdValue', label: 'Next Value' },
      ]}
      csvFilename="student-id-formats.csv"
    />
  );
};

export default StudentIdFormat;